import { makeAutoObservable } from "mobx";
import { ZUid } from "src/types/zUid";
import { TableStore } from "src/components/tables/TableStore";
import { singleLoader } from "src/components/tables/singleLoader";
import { ZObjState } from "src/types/ZObjState";
import { makeDictionary } from "src/common/makeDictionary";
import { refSorter, strFieldSorter } from "src/components/tables/clientSorting";
import { onError } from "src/common/onError";
import { callChangeEntityState } from "src/pages/EntityCardPage/apiEntityCard";
import { loadObjectStates } from "src/pages/ManagementPage/Obj2Tab/roles/rolesApi";
import {
  createMc2,
  deleteMcs2,
  loadMc2Permissions,
  loadMc2Settings,
  loadMc2Summary,
  updateMc2,
} from "../apiMc2";
import { FilterMc2, Mc2Fields, ZMc2, ZMc2Settings } from "../Mc2Types";
import { Mc2EntityStore } from "./Mc2Entity";
import { Mc2CopyToStore } from "./actions/Mc2CopyTo";
import { Mc2CopyFromStore } from "./actions/Mc2CopyFrom";
import { hasMc2Permission, Mc2Permissions } from "../mc2Permissions";
import { loadSizeLineAttrNameId } from "./loadSizeLineAttrNameId";

/* eslint no-plusplus: "off" */

export const mc2SummaryStore = makeAutoObservable({
  tableStore: null as TableStore<ZMc2, FilterMc2> | null,
  setTableStore(store: TableStore<ZMc2, FilterMc2> | null) {
    this.tableStore = store;
  },
  rows: [] as ZMc2[],
  setRows(list: ZMc2[]) {
    this.rows = list;
  },
  lastError: null as Error | null,
  setLastError(e: Error | null) {
    this.lastError = e;
  },
  lockCounter: 0,
  lock() {
    this.lockCounter++;
  },
  unlock() {
    this.lockCounter--;
  },
  get buzy(): boolean {
    return this.lockCounter !== 0;
  },
  modelEntityId: "" as ZUid,
  setModelEntityId(id: ZUid) {
    this.modelEntityId = id;
  },
  modelObjectId: "" as ZUid,
  setModelObjectId(id: ZUid) {
    this.modelObjectId = id;
  },

  settings: {} as ZMc2Settings,
  setSettings(data: ZMc2Settings) {
    this.settings = data;
  },

  states: [] as ZObjState[],
  setStates(list: ZObjState[]) {
    this.states = list;
  },
  get statesDict(): Record<ZUid, ZObjState> {
    return makeDictionary(this.states, (item) => item.id);
  },
  // для клиентской сортировки
  get statesDictStr(): Record<string, string> {
    return this.states.reduce(
      (acc, st) => ({
        ...acc,
        [String(st.id)]: st.name,
      }),
      {} as Record<string, string>,
    );
  },

  canCreate: false,
  setCanCreate(value: boolean) {
    this.canCreate = value;
  },
  get createReason(): string {
    if (!this.canCreate) return "Нет прав на создание";
    return "";
  },

  sizeLineAttrNameId: "",
  setSizeLineAttrNameId(id: string) {
    this.sizeLineAttrNameId = id;
  },

  async init(params: { modelObjectId: ZUid; modelEntityId: ZUid }) {
    this.entityStore = null;
    this.setModelEntityId(params.modelEntityId);
    this.setModelObjectId(params.modelObjectId);
    this.setSizeLineAttrNameId("");
    this.setTableStore(null);
    this.setRows([]);
    try {
      this.lock();
      const [settings, permissions] = await Promise.all([
        loadMc2Settings(params.modelObjectId),
        loadMc2Permissions(params.modelObjectId),
      ]);
      this.setSettings(settings);
      this.setCanCreate(permissions.includes(Mc2Permissions.create));
      if (this.states.length === 0) {
        this.setStates(await loadObjectStates(settings.objMcId!));
      }
      this.setSizeLineAttrNameId(
        await loadSizeLineAttrNameId(settings.objSizeLineId),
      );
      await this.loadRows();
    } finally {
      this.unlock();
    }
  },

  async loadRows() {
    try {
      this.setLastError(null);
      this.lock();
      const allRows = await loadMc2Summary(this.modelEntityId);
      this.setRows(allRows);
      this.setTableStore(
        new TableStore({
          rowKey: "id",
          fnLoad: singleLoader(
            async () => allRows,
            () => true,
            {
              name: strFieldSorter("name"),
              state: refSorter("stateId", this.statesDictStr),
              sizeLineName: strFieldSorter("sizeLineName"),
            },
          ),
        }),
      );
    } catch (e) {
      this.setLastError(e);
    } finally {
      this.unlock();
    }
  },
  async update(task: () => Promise<void>) {
    try {
      this.lock();
      await task();
      await this.loadRows();
    } finally {
      this.unlock();
    }
  },

  mode: null as Mc2SummaryMode,
  setMode(newMode: Mc2SummaryMode) {
    this.mode = newMode;
  },

  async createMc(data: Mc2Fields) {
    await this.update(async () => {
      await createMc2(this.modelEntityId, data);
    });
  },

  get rowForEdit(): ZMc2 | undefined {
    const selected = this.tableStore?.selected;
    return selected && selected.length === 1 ? selected[0] : undefined;
  },
  get editReason(): string {
    const row = this.rowForEdit;
    if (!row) return "Должна быть выбрана одна строка";
    if (!hasMc2Permission(row, Mc2Permissions.update))
      return "Нет прав на редактирование";
    return "";
  },
  startEdit() {
    const { rowForEdit } = this;
    if (!rowForEdit) return;
    this.setMode("edit");
  },
  async finalEdit(data: Mc2Fields) {
    const { rowForEdit } = this;
    if (!rowForEdit) return;
    await this.update(async () => {
      const { id } = rowForEdit;
      await updateMc2(id, { ...data, id });
    });
  },

  get deleteReason(): string {
    const rows = this.tableStore?.selected ?? [];
    if (rows.length === 0) return "Должна быть выбрана хотя бы одна строка";
    const denied = rows.find(
      (row) => !hasMc2Permission(row, Mc2Permissions.delete),
    );
    if (denied) return "Нет прав на удаление";
    return "";
  },
  get canDelete(): boolean {
    return !this.deleteReason && !this.buzy;
  },
  startDelete() {
    if (!this.canDelete) return;
    this.setMode("delete");
  },
  async finalDelete() {
    const ids = this.tableStore?.selected.map(({ id }) => id);
    if (!ids?.length) return;
    await this.update(async () => {
      await deleteMcs2(ids);
      this.setMode(null);
    });
  },

  // MC Entity
  entityStore: null as Mc2EntityStore | null,
  openEntity(mc: ZMc2) {
    this.entityStore = new Mc2EntityStore(mc, this.settings, () => {
      this.loadRows().catch(onError);
    });
  },
  closeEntity() {
    this.entityStore = null;
  },

  copyToStore: new Mc2CopyToStore(),
  copyTo() {
    const selectedMcIds = this.tableStore?.selected.map(({ id }) => id) ?? [];
    if (selectedMcIds.length === 0) return;
    this.copyToStore.init({
      modelObjectId: this.modelObjectId,
      modelEntityId: this.modelEntityId,
      mcIds: selectedMcIds,
      onFinish: (needReload) => {
        this.tableStore?.setSelected([]);
        if (needReload) {
          this.loadRows().catch(onError);
        }
      },
    });
  },

  copyFromStore: new Mc2CopyFromStore(),
  copyFrom() {
    this.copyFromStore.init({
      modelObjectId: this.modelObjectId,
      dstModelEntityId: this.modelEntityId,
      onFinish: () => {
        this.loadRows().catch(onError);
      },
    });
  },

  // states
  get statesSet(): Set<ZUid> {
    return new Set(
      (this.tableStore?.selected ?? [])
        .map(({ stateId }) => stateId)
        .filter((id) => !!id) as ZUid[],
    );
  },
  get statesReason(): string {
    const states = Array.from(this.statesSet);
    if (states.length === 0) {
      return "Требуется выбрать хотя бы одну строку с состоянием";
    }
    if (states.length > 1) {
      return "Требуется, чтобы были выбраны строки с одинаковым состоянием";
    }
    // Проверка пермишенов отключена по требованию LPLM-4522
    // const state = states[0];
    // if (state) {
    //   const denied = this.tableStore?.selected.find(
    //     (row) => !hasMc2Permission(row, Mc2Permissions.updateState),
    //   );
    //   if (denied) return "Нет прав на изменение состояния";
    // }
    return "";
  },
  async changeState(dstStateId: ZUid) {
    const entities = this.tableStore?.selected ?? [];
    if (entities.length === 0) return;
    // По-хорошему, нужна смена для нескольких экземпляров одним запросом.
    // Но пока такого запроса нет.
    // Цикл использован потому что Promise.all может создать взаимные помехи
    /* eslint no-restricted-syntax: "off" */
    /* eslint no-await-in-loop: "off" */
    for (const entity of entities) {
      await callChangeEntityState(entity.id, dstStateId);
    }
    await this.loadRows();
  },
});

type Mc2SummaryMode = "create" | "delete" | "edit" | "states" | null;
